// An operator's own changes to an action from its row: push its date, or resolve it with a
// reason. Each is recorded as theirs and shown to the owning agent on its next run.
// See docs/Y2026-Q4/design-action-dates-dependencies-and-ranking.md 5.4 and 6.
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import {
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import {
  type OperatorChange,
  useOperatorChange,
} from "../api/hooks/useActionChanges";
import type { NextAction } from "../api/hooks/useProjectAgentStatus";
import { changeSaved, useSentNotice } from "../state/sentNotice";
import { formatDay, isoDay, localDay } from "../utils/attributeFormat";
import type { BulkPush } from "../utils/bulkActions";
import {
  RESOLVE_REASON_MAX,
  needsOptionChoice,
  optionLines,
  pushBlocker,
  pushTarget,
} from "../utils/nextActions";
import { useAnchoredPlacement } from "../utils/useAnchoredPlacement";
import { useClickOutside } from "../utils/useClickOutside";
import styles from "./NextActionTable.module.css";
import own from "./ActionControls.module.css";

const PUSH_DAYS = [3, 7, 14, 30] as const;

/** A card floating under the button that opened it, over the page rather than in its
 * flow, so nothing below moves. In the body, fixed to the button: the actions table's
 * scroll box would clip it. It follows the button on a scroll and closes on Escape or a
 * click elsewhere. */
export const Flyout = ({
  anchor,
  className,
  role,
  onClose,
  children,
}: {
  anchor: RefObject<HTMLElement>;
  className: string;
  role?: string;
  onClose: () => void;
  children: ReactNode;
}) => {
  const card = useClickOutside<HTMLDivElement>(true, onClose, [anchor]);
  const { place, position } = useAnchoredPlacement(true, anchor, card);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", position, true);
    window.addEventListener("resize", position);
    return () => {
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", position, true);
      window.removeEventListener("resize", position);
    };
  }, [onClose, position]);
  return createPortal(
    <div
      ref={card}
      className={`${own.flyout} ${className}`}
      role={role}
      style={place}
    >
      {children}
    </div>,
    document.body,
  );
};

const chevron = (open: boolean) =>
  `${own.chevron} ${open ? own.chevronOpen : ""}`;

/** What a push is for: one action (``onPush`` gets the date chosen), or each of several
 * ticked actions (``onPush`` gets "by N days from its own date" or "all to one date"). */
export type PushTarget =
  | { scope: "one"; due: string; onPush: (date: string) => void }
  | { scope: "each"; onPush: (push: BulkPush) => void };

/** "Push by 7 days" with a menu of other pushes (tomorrow, a number of days on) and a custom
 * date, which shows a calendar with the current date ringed once chosen; closing the menu
 * folds it away. Only the calendar offers a Sunday, for the operator to pick on purpose; the
 * other pushes land on working days. The server moves a date that lands on a bank holiday
 * to a working day. */
export const PushControl = ({
  target,
  today,
  busy,
  label = "Push",
}: {
  target: PushTarget;
  today: string;
  busy: boolean;
  label?: string;
}) => {
  const [menu, setMenu] = useState(false);
  const [custom, setCustom] = useState(false);
  const [picked, setPicked] = useState("");
  const anchor = useRef<HTMLSpanElement>(null);
  const due = target.scope === "one" ? target.due : "";
  const pushBy = (days: number) => pushTarget(due, today, days);
  // The next working day: on a Friday, the Monday.
  const tomorrow = pushTarget("", today, 1);
  const by = (days: number) =>
    target.scope === "one" ? target.onPush(pushBy(days)) : target.onPush({ by: days });
  const to = (date: string) =>
    target.scope === "one" ? target.onPush(date) : target.onPush({ to: date });
  const verb = target.scope === "one" ? label : `${label} each`;
  const closeMenu = useCallback(() => {
    setMenu(false);
    setCustom(false);
    setPicked("");
  }, []);
  return (
    <>
      <span ref={anchor} className={own.split}>
        <button
          type="button"
          className={`${styles.button} ${own.splitMain}`}
          disabled={busy}
          onClick={() => by(7)}
        >
          {verb} by 7 days
        </button>
        <button
          type="button"
          className={`${styles.button} ${own.splitToggle} ${chevron(menu)}`}
          aria-label="Other ways to push"
          aria-expanded={menu}
          disabled={busy}
          onClick={() => (menu ? closeMenu() : setMenu(true))}
        />
      </span>
      {menu && (
        <Flyout
          anchor={anchor}
          className={own.menu}
          role="menu"
          onClose={closeMenu}
        >
          {tomorrow !== due && (
            <button
              type="button"
              role="menuitem"
              className={own.menuItem}
              onClick={() => {
                closeMenu();
                to(tomorrow);
              }}
            >
              <span>
                {target.scope === "one"
                  ? `${label} to tomorrow`
                  : `${label} all to tomorrow`}
              </span>
              <span className={styles.small}>to {formatDay(tomorrow)}</span>
            </button>
          )}
          {PUSH_DAYS.map((days) => (
            <button
              key={days}
              type="button"
              role="menuitem"
              className={own.menuItem}
              onClick={() => {
                closeMenu();
                by(days);
              }}
            >
              <span>
                {verb} by {days} days
              </span>
              <span className={styles.small}>
                {target.scope === "one"
                  ? `to ${formatDay(pushBy(days))}`
                  : "from its own due date"}
              </span>
            </button>
          ))}
          <button
            type="button"
            role="menuitem"
            className={own.menuItem}
            aria-expanded={custom}
            onClick={() => setCustom((open) => !open)}
          >
            {target.scope === "one"
              ? `${label} to a custom date…`
              : `${label} all to one date…`}
          </button>
          {custom && (
            <span className={own.custom}>
              <DatePicker
                inline
                calendarClassName={own.calendar}
                calendarStartDay={1}
                selected={picked ? localDay(picked) : null}
                openToDate={localDay(picked || (due > today ? due : today))}
                minDate={localDay(today)}
                // Not a Saturday, nor the date it is already due.
                filterDate={(day) => day.getDay() !== 6 && isoDay(day) !== due}
                highlightDates={due ? [localDay(due)] : []}
                onChange={(date: Date | null) =>
                  setPicked(date ? isoDay(date) : "")
                }
              />
              {due && (
                <span className={styles.small}>
                  Ringed: now due {formatDay(due)}
                </span>
              )}
              <button
                type="button"
                className={`${styles.button} ${styles.primary}`}
                disabled={!picked}
                onClick={() => {
                  closeMenu();
                  to(picked);
                }}
              >
                {!picked
                  ? "Pick a date"
                  : target.scope === "one"
                    ? `${label} to ${formatDay(picked)}`
                    : `${label} all to ${formatDay(picked)}`}
              </button>
            </span>
          )}
        </Flyout>
      )}
    </>
  );
};

/** Done or no longer needed, with the operator's reason, and the decision's option when a
 * decision is done. It is dated the day the operator resolves it; a different day the
 * thing happened goes in the reason. ``action`` null resolves several ticked actions with
 * one reason, where a decision's option cannot be asked. */
const ResolveForm = ({
  action,
  busy,
  submitLabel,
  onResolve,
  onCancel,
}: {
  action: NextAction | null;
  busy: boolean;
  submitLabel: string;
  onResolve: (change: OperatorChange & { kind: "resolve" }) => void;
  onCancel: () => void;
}) => {
  const [outcome, setOutcome] = useState<"done" | "dropped">("done");
  const [reason, setReason] = useState("");
  const options = action ? optionLines(action.call_to_action) : new Map<number, string>();
  const [option, setOption] = useState<number | null>(null);
  const needsOption = action !== null && needsOptionChoice(action, outcome);
  const ready = reason.trim() !== "" && (!needsOption || option !== null);
  return (
    <>
      <div role="group" aria-label="Outcome" className={own.outcomes}>
        {(
          [
            ["done", action?.type === "decision" ? "Decided" : "Done"],
            ["dropped", "No longer needed"],
          ] as const
        ).map(([value, text]) => (
          <button
            key={value}
            type="button"
            className={own.outcome}
            aria-pressed={outcome === value}
            onClick={() => setOutcome(value)}
          >
            {text}
          </button>
        ))}
      </div>
      {needsOption && (
        <label className={own.field}>
          Which option
          <select
            value={option ?? ""}
            onChange={(e) =>
              setOption(e.target.value ? Number(e.target.value) : null)
            }
          >
            <option value="">Choose…</option>
            {[...options].map(([number, line]) => (
              <option key={number} value={number}>
                {number}. {line}
              </option>
            ))}
          </select>
        </label>
      )}
      <label className={own.field}>
        {action ? "Why it is resolved" : "Why they are resolved"}
        <textarea
          rows={2}
          value={reason}
          maxLength={RESOLVE_REASON_MAX}
          placeholder="Agreed by phone with the landlord on 10 Oct"
          onChange={(e) => setReason(e.target.value)}
        />
      </label>
      <div className={own.formButtons}>
        <button
          type="button"
          className={`${styles.button} ${styles.primary}`}
          disabled={busy || !ready}
          onClick={() =>
            onResolve({
              kind: "resolve",
              outcome,
              reason: reason.trim(),
              ...(needsOption && option !== null
                ? { chosen_option: option }
                : {}),
            })
          }
        >
          {busy ? "Resolving…" : submitLabel}
        </button>
        <button type="button" className={styles.button} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </>
  );
};

/** "Resolve" opening the form under it. ``open`` is the caller's, so one action's form
 * stays open until its write lands, and a bulk choice closes it at once. */
export const ResolveControl = ({
  action,
  open,
  onOpenChange,
  busy,
  submitLabel,
  onResolve,
}: {
  action: NextAction | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  busy: boolean;
  submitLabel: string;
  onResolve: (change: OperatorChange & { kind: "resolve" }) => void;
}) => {
  const button = useRef<HTMLButtonElement>(null);
  const close = useCallback(() => onOpenChange(false), [onOpenChange]);
  return (
    <>
      <button
        ref={button}
        type="button"
        className={`${styles.button} ${chevron(open)}`}
        aria-expanded={open}
        disabled={busy && !open}
        onClick={() => onOpenChange(!open)}
      >
        Resolve
      </button>
      {open && (
        <Flyout anchor={button} className={own.resolve} onClose={close}>
          <ResolveForm
            action={action}
            busy={busy}
            submitLabel={submitLabel}
            onResolve={onResolve}
            onCancel={close}
          />
        </Flyout>
      )}
    </>
  );
};

/** Push and Resolve for one open action, written as the operator against the version of
 * the agent's lists the page shows. ``onChanged`` runs once a push or resolve has landed:
 * the row folds away (Noaa, 28 Oct 2026), while a refused write leaves it open with its
 * error. */
export const ActionControls = ({
  action,
  agent,
  projectId,
  version,
  today,
  pushable,
  onChanged,
}: {
  action: NextAction;
  agent: string;
  projectId: string;
  /** The version of the agent's lists the page shows. */
  version: number;
  today: string;
  /** False while an agent's proposal to move the date is open: the answer moves it. */
  pushable: boolean;
  onChanged: () => void;
}) => {
  const { user } = useKindeAuth();
  const change = useOperatorChange();
  const showSent = useSentNotice();
  const [resolving, setResolving] = useState(false);
  const send = (body: OperatorChange) =>
    change.mutate(
      {
        project_id: projectId,
        agent,
        seen_version: version,
        changes: [{ item_id: action.action_id, change: body }],
        actor_email: user?.email ?? "",
        // Saved at once and shown at once; the agent takes it in on its next daily run
        // rather than a review now, which costs a run and rewrites the lists under the
        // page (Noaa, 29 Oct 2026).
        wake: false,
      },
      {
        onSuccess: () => {
          setResolving(false);
          showSent(changeSaved());
          onChanged();
        },
      },
    );
  return (
    <>
      {action.due && pushBlocker(action, !pushable) === null && (
        <PushControl
          target={{
            scope: "one",
            due: action.due,
            onPush: (date) => send({ kind: "push", date }),
          }}
          today={today}
          busy={change.isLoading}
        />
      )}
      <ResolveControl
        action={action}
        open={resolving}
        onOpenChange={setResolving}
        busy={change.isLoading}
        submitLabel="Resolve"
        onResolve={send}
      />
      {change.error && <p className={own.error}>{change.error.message}</p>}
    </>
  );
};
